import { useState } from "react"

import { useForm } from "@tanstack/react-form"

import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"

interface CurriculumStepProps {
  value: string[]
  onChange: (value: string[]) => void
  onValidation?: (isValid: boolean) => void
}

const CURRICULUM_OPTIONS = [
  { value: "CKLA", label: "CKLA" },
  { value: "Pearson", label: "Pearson" },
  { value: "CambriLearn", label: "CambriLearn" },
  { value: "i-ready", label: "i-ready" },
  { value: "McGraw Hill", label: "McGraw Hill" },
  { value: "Great Minds", label: "Great Minds" },
  { value: "HMH", label: "HMH" },
  { value: "Discovery K12", label: "Discovery K12" },
  { value: "Other", label: "Other ✍" },
]

export function CurriculumStep({
  value,
  onChange,
  onValidation,
}: CurriculumStepProps) {
  const [showOtherInput, setShowOtherInput] = useState(false)
  const [otherValue, setOtherValue] = useState("")

  const form = useForm({
    defaultValues: {
      curriculum: value,
    },
  })

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  const handleOptionClick = (optionValue: string, field: any) => {
    const currentValues = field.state.value || []

    if (optionValue === "Other") {
      if (showOtherInput) {
        // Remove "Other" option
        setShowOtherInput(false)
        setOtherValue("")
        const newValues = currentValues.filter(
          (v: string) =>
            !CURRICULUM_OPTIONS.find((opt) => opt.value === "Other")?.value ||
            v !== "Other",
        )
        field.handleChange(newValues)
        onChange(newValues)
        onValidation?.(newValues.length > 0)
      } else {
        // Show "Other" input
        setShowOtherInput(true)
        onValidation?.(false)
      }
    } else {
      // Toggle selection for predefined options
      const isSelected = currentValues.includes(optionValue)
      const newValues = isSelected
        ? currentValues.filter((v: string) => v !== optionValue)
        : [...currentValues, optionValue]

      field.handleChange(newValues)
      onChange(newValues)
      onValidation?.(newValues.length > 0)
    }
  }

  const isSelected = (optionValue: string, fieldValue: string[]) => {
    if (optionValue === "Other") {
      return showOtherInput
    }
    return fieldValue?.includes(optionValue) || false
  }

  return (
    <div className="tw-text-center tw-space-y-8">
      <h2 className="tw-text-3xl tw-font-bold tw-text-gray-800">
        What curriculum is this work based on?
      </h2>
      <p className="tw-text-sm tw-text-gray-600">Select all that apply</p>

      <form.Field name="curriculum">
        {(field) => {
          return (
            <div className="tw-max-w-3xl tw-mx-auto tw-space-y-6">
              <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                {CURRICULUM_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    variant="selectable"
                    size="card"
                    selected={isSelected(option.value, field.state.value)}
                    onClick={() => handleOptionClick(option.value, field)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>

              {showOtherInput && (
                <div className="tw-max-w-md tw-mx-auto tw-mt-6">
                  <Input
                    type="text"
                    placeholder="Enter curriculum name..."
                    value={otherValue}
                    onChange={(e) => {
                      const value = e.target.value
                      setOtherValue(value)

                      if (value.trim() !== "") {
                        const currentValues = field.state.value || []
                        const filteredValues = currentValues.filter(
                          (v: string) =>
                            CURRICULUM_OPTIONS.some((opt) => opt.value === v),
                        )
                        const newValues = [...filteredValues, value.trim()]
                        field.handleChange(newValues)
                        onChange(newValues)
                        onValidation?.(true)
                      } else {
                        onValidation?.(field.state.value?.length > 0)
                      }
                    }}
                    className="tw-w-full tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-border-gray-300 focus:tw-border-green-500 focus:tw-outline-none tw-text-center"
                    autoFocus
                  />
                </div>
              )}
            </div>
          )
        }}
      </form.Field>
    </div>
  )
}
